/**
 * Minimal toast notification system. Replaces native `alert()` for state-change
 * feedback. Stack of up to 3 toasts; auto-dismiss after 3.5s; click to dismiss.
 *
 * Usage:
 *   showToast('eSIM cancelled');
 *   showToast('Payment failed', 'error');
 *   showToast('Top-up confirmed', 'success');
 *
 * Inside a Telegram Mini App, each toast triggers a matching HapticFeedback
 * pattern so success/error/info are felt as well as seen. No-op on web.
 */
import { haptic as tgHaptic } from './telegram-mini-app';

export type ToastKind = 'info' | 'success' | 'error';

const STACK_LIMIT = 3;
const DEFAULT_TTL_MS = 3500;

let container: HTMLElement | null = null;

function getContainer(): HTMLElement {
  if (container && document.body.contains(container)) return container;
  container = document.createElement('div');
  container.className = 'toast-stack';
  // a11y: announce toasts politely to screen readers without hijacking focus.
  container.setAttribute('role', 'status');
  container.setAttribute('aria-live', 'polite');
  container.setAttribute('aria-atomic', 'false');
  document.body.appendChild(container);
  return container;
}

export function showToast(message: string, kind: ToastKind = 'info', ttlMs: number = DEFAULT_TTL_MS): void {
  // Tactile pairing for toasts — gives TG users an unmistakable signal
  // when the visual toast might be glanced at half-asleep. Web: no-op.
  if (kind === 'success') tgHaptic.success();
  else if (kind === 'error') tgHaptic.error();
  else tgHaptic.tapLight();  // 'info' → soft selection-style nudge

  const root = getContainer();
  // Drop oldest if stack is full
  while (root.children.length >= STACK_LIMIT) {
    root.firstElementChild?.remove();
  }
  const el = document.createElement('div');
  el.className = `toast-item toast-item--${kind}`;
  el.textContent = message;
  el.addEventListener('click', () => el.remove());
  root.appendChild(el);
  // Trigger CSS enter animation
  requestAnimationFrame(() => el.classList.add('toast-item--in'));
  window.setTimeout(() => {
    el.classList.remove('toast-item--in');
    el.classList.add('toast-item--out');
    window.setTimeout(() => el.remove(), 300);
  }, ttlMs);
}
